/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, memo, useMemo } from "react";
import type * as type from "@vTypes";
import { useRosterValidation } from "@hooks/useRosterValidation.ts";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { useRosterSubmissionQuery } from "@hooks/roster-craete/useRosterSubmissionQuery";
import { useScheduleStore } from "@store/scheduleStore";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useGroupStore } from "@store/groupStore.ts";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import type { CellViolationInfo } from "@/services/validation";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import { nurseGroupSortKey } from "@utils/common-util.ts";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
type TableBodyBaseProps = {
  copyNurses: any;
  setCopyNurses: (value: any) => void;
  availableShifts: type.availableShifts[];
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  saveNurseSequence: (nurse_id: string, newSequence: number) => Promise<void>;
  currentScheduleInfo:
    | { year: number; month: number; version: number }
    | undefined;
  currentScheduleID: string | undefined;
  isDragEditing: boolean;
  selectedGroupId?: string | null;
  manPower_monthly: type.ManPowerMonthly | null;
  manPower_daily?: type.ManPowerDaily | null;
  useMaxManpower?: boolean;
  enableMaxManpowerViolation?: boolean;
  year: number;
  month: number;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  disableManpowerValidation?: boolean;
  onlyView?: boolean;
  /** false면 react-sortablejs 없이 목록만 렌더 (근무표 만들기: 행 드래그 정렬 미사용) */
  enableRowDragSort?: boolean;
  RowComponent: React.ComponentType<any>;
  prevTailData?: PrevTailData | null; // 전월 말일 근무표 데이터
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  showPerShiftStats?: boolean;
  violationVisibilityOverride?: {
    showConfigViolations: boolean;
    showManpowerViolations: boolean;
  } | null;
  /** false: 셀·헤더 클릭으로 열·행 선택(클릭 하이라이트) 안 함 */
  selectionFocusOnClick?: boolean;
  /** 스케줄 응답의 year/month — 그리드 year/month 와 일치할 때만 inbound 바 표시 */
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  /** 현재 편집 중인 병동 group_id — inbound 잠금 정책에 사용 */
  currentGroupId?: string | null;
};

function TableBody_base({
  copyNurses,
  availableShifts,
  isEditMode,
  selectedShift,
  modDayShift,
  currentScheduleInfo,
  currentScheduleID,
  isDragEditing,
  selectedGroupId,
  manPower_monthly,
  manPower_daily,
  useMaxManpower = false,
  enableMaxManpowerViolation = true,
  year,
  month,
  selectIdForSideprofile,
  disableManpowerValidation = false,

  RowComponent,
  prevTailData,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  showPerShiftStats = false,
  violationVisibilityOverride,
  selectionFocusOnClick = true,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId,
}: TableBodyBaseProps) {
  const { curDate } = useScheduleStore();
  const isAdminHook = useIsAdmin();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();

  const gid = isAdminHook
    ? (selectedGroupId ?? selectedGroupIdFromStore ?? "")
    : "";

  const { data: submissionData } = useRosterSubmissionQuery({
    year: curDate?.year,
    month: curDate?.month,
    groupId: isAdminHook
      ? (selectedGroupId ?? selectedGroupIdFromStore ?? "")
      : "",
  });
  const currentSubmittedList = submissionData?.submitted_nurses ?? [];

  const { data: serverConfig } = useRosterConfig();
  const { data: shiftsData } = useShiftsQuery(gid);

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    availableShifts,
    shiftsData ?? null
  );

  const precepteePolicy = getPrecepteePolicy(serverConfig);
  const isUseMid = serverConfig?.use_mid;

  const filteredNurses = useMemo(() => {
    return copyNurses?.filter((nurse: any) =>
      precepteePolicy.excludePrecepteeFromCount
        ? nurse.preceptor?.role !== "preceptee"
        : true
    );
  }, [copyNurses, precepteePolicy.excludePrecepteeFromCount]);


  const { cellViolationMap, validate } = useRosterValidation({
    currentScheduleInfo,
    currentScheduleID,
    copyNurses: filteredNurses,
    isDragEditing,
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    enableMaxManpowerViolation,
    year,
    month,
    serverConfig: serverConfig ?? null,
    defaultShiftCode: availableShifts,
    shiftsData: shiftsData ?? [],
    disableManpowerValidation,
    prevTailData,
    currentGroupId,
  });

  useEffect(() => {
    if (!isDragEditing) {
      validate();
    }
  }, [copyNurses, currentScheduleID, isDragEditing, validate]);

  const {
    columnShiftIds: statsColumnShiftIds,
    displayShifts: statsDisplayShifts,
  } = useRosterDisplayShiftColumns(
    copyNurses ?? [],
    availableShifts,
    isUseMid,
    {
      /** 기본/원티드 모두: 메인 default 템플릿(D/E/N…)은 사용량 0이어도 우측 통계열 유지 */
      includeBaselineMainTemplateColumns: true,
    }
  );

  useEffect(() => {
    const clickWindow = (e: Event) => {
      const clickTarget = e.target as HTMLElement;
      if (!clickTarget.closest(".preceptorMark")) {
        document
          .querySelectorAll(".preceptorGroup")
          .forEach((tr) => tr.classList.remove("click"));
      }
    };
    window.addEventListener("click", clickWindow);
    return () => {
      window.removeEventListener("click", clickWindow);
    };
  }, []);

  const clearSelectedFocus = useRosterFocusStore(
    (state) => state.clearSelectedFocus
  );

  useEffect(() => {
    /** capture: 모달 등에서 `stopPropagation`(버블 차단)돼도 바깥 클릭 시 포커스 해제되도록 */
    const clearWhenOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        !target?.closest("[data-roster-shift-cell]") &&
        !target?.closest("[data-roster-header-day]")
      ) {
        clearSelectedFocus();
      }
    };
    window.addEventListener("click", clearWhenOutside, true);
    return () => window.removeEventListener("click", clearWhenOutside, true);
  }, [clearSelectedFocus]);

  /** 스케줄·월 변경 시 열·행 선택 일괄 해제(다른 근무표로 포커스 잔상 방지) */
  useEffect(() => {
    clearSelectedFocus();
  }, [currentScheduleID, year, month, clearSelectedFocus]);

  useEffect(() => {
    return () => {
      clearSelectedFocus();
    };
  }, [clearSelectedFocus]);

  const renderNurseList = () => {
    return copyNurses.map((nurse: any, index: number) => {
      const isSubmitted = currentSubmittedList?.includes(nurse?.nurse_id);
      const nextNurse = copyNurses[index + 1];
      const roleGroupBottomBorder =
        nextNurse != null &&
        nurseGroupSortKey(nurse) !== nurseGroupSortKey(nextNurse);

      return (
        <RowComponent
          key={String(nurse?.nurse_id ?? nurse?.id ?? `nurse-row-${index}`)}
          isEditMode={isEditMode}
          nursesIndex={index}
          nurse={nurse}
          roleGroupBottomBorder={roleGroupBottomBorder}
          availableShifts={availableShifts}
          shiftLookupMaps={shiftLookupMaps}
          isSubmitted={isSubmitted}
          modDayShift={modDayShift}
          selectedShift={selectedShift}
          cellViolationMap={
            cellViolationMap as unknown as Map<string, CellViolationInfo>
          }
          statsColumnShiftIds={statsColumnShiftIds}
          statsDisplayShifts={statsDisplayShifts}
          selectIdForSideprofile={selectIdForSideprofile}
          disableManpowerValidation={disableManpowerValidation}
          copyNurses={copyNurses}
          prevTailData={prevTailData}
          isEmergencyPickMode={isEmergencyPickMode}
          onEmergencySlotPick={onEmergencySlotPick}
          emergencySlotPick={emergencySlotPick}
          isEmergencyBulkPickMode={isEmergencyBulkPickMode}
          onEmergencyBulkPick={onEmergencyBulkPick}
          emergencyBulkPeriodPick={emergencyBulkPeriodPick}
          emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
          emergencyReplacementCandidateNurseId={
            emergencyReplacementCandidateNurseId
          }
          emergencyPostApplyHighlight={emergencyPostApplyHighlight}
          showPerShiftStats={showPerShiftStats}
          scheduleYear={year}
          scheduleMonth={month}
          schedulePayloadYear={schedulePayloadYear}
          schedulePayloadMonth={schedulePayloadMonth}
          currentGroupId={currentGroupId}
          violationVisibilityOverride={violationVisibilityOverride}
          selectionFocusOnClick={selectionFocusOnClick}
        />
      );
    });
  };

  return <> {renderNurseList()}</>;
}
export default memo(TableBody_base);
